import * as React from "react";

import { type DownloadPriority, type Message } from "@/shared/reader";
import { ErrorMonitoringService } from "@/shared/services";
import { LayoutItem, RawMessagePanelState } from "@/shared/state/visualization";

import { RawMessagePanelSettingsSidebarProps } from "../../PanelSettings/RawMessagePanelSettings";
import { SidebarViewType, useWorkspaceSidebar } from "../../sidebarContext";
import { useWorkspaceTimer } from "../../WorkspaceCtx";
import { useSelectPanel } from "../../WorkspaceCtx/hooks";
import { DataNotFoundMessage } from "../DataNotFoundMessage";
import { NoDataMessage } from "../NoDataMessage";
import { NoRecordedDataMessage } from "../NoRecordedDataMessage";
import { PanelHeader } from "../PanelHeader";
import { PanelLayout } from "../PanelLayout";

import { FormattedMessage } from "./FormattedMessage";
import { RawMessageManager } from "./RawMessageManager";
import { useMessages } from "./useMessages";

interface RawMessagePanelProps {
  layout: LayoutItem;
  state: RawMessagePanelState;
}

export function RawMessagePanel({ layout, state }: RawMessagePanelProps) {
  const timer = useWorkspaceTimer();
  const sidebar = useWorkspaceSidebar();
  const selectPanel = useSelectPanel();

  const [loadingState, setLoadingState] = React.useState<
    DownloadPriority | false
  >("foreground");
  const [error, setError] = React.useState<Error | null>(null);
  const [message, setMessage] = React.useState<Message | null>(null);
  const timerHoldRef = React.useRef<symbol | null>(null);

  // --- Manager lifecycle ---
  const [manager, setManager] = React.useState<RawMessageManager | null>(null);

  React.useEffect(
    function initManager() {
      const abortController = new AbortController();
      const _manager = new RawMessageManager();

      _manager.setOnInitialized(() => {
        if (abortController.signal.aborted) {
          return;
        }
        setManager(_manager);
      });

      _manager.setOnError((err) => {
        if (abortController.signal.aborted) {
          return;
        }
        setError(err);
      });

      _manager.setOnMessage((msg) => {
        if (abortController.signal.aborted) {
          return;
        }
        setError(null);
        setMessage(msg);
      });

      return function cleanup() {
        abortController.abort();
        _manager.dispose();
      };
    },
    [], // Mount/unmount only
  );

  // --- Timer interaction ---
  React.useEffect(
    function updateTimer() {
      if (manager === null) {
        return;
      }

      const abortController = new AbortController();
      manager.setTimer(timer, abortController);

      manager.setOnLoadingChange((state: DownloadPriority | false) => {
        if (abortController.signal.aborted) {
          return;
        }
        // Hold the workspace timer while any fetch is in flight, regardless
        // of whether the user-facing spinner is showing. Background prefetch
        // can still stall playback past the cached range, so pausing the
        // playhead until the worker catches up keeps the rendered message
        // aligned with the timer's reported time. The spinner itself is
        // gated on the "foreground" priority downstream in `PanelLayout`.
        const isLoading = state !== false;
        if (isLoading && timerHoldRef.current === null) {
          timerHoldRef.current = timer.hold();
        }
        if (!isLoading && timerHoldRef.current !== null) {
          timer.holdRelease(timerHoldRef.current);
          timerHoldRef.current = null;
        }
        setLoadingState(state);
      });

      return function abort() {
        abortController.abort();
        if (timerHoldRef.current !== null) {
          timer.holdRelease(timerHoldRef.current);
          timerHoldRef.current = null;
        }
      };
    },
    [manager, timer],
  );

  // --- Drive message playback ---
  // Declared after the timer-binding effect so React runs that effect first
  // on each `manager` change, ensuring `manager.timer` is set before the
  // poller starts calling `loadMessages`. The poll loop additionally
  // short-circuits until the worker delivers a timestamp index, so the
  // `setState` push to the worker in `syncState` below can land in any
  // order without the poller observing a half-initialized manager.
  useMessages(manager);

  // --- Send state to worker when topic data changes ---
  React.useEffect(
    function syncState() {
      if (manager === null) {
        return;
      }

      const abortController = new AbortController();
      manager.setState(state.data, abortController.signal);

      return function cleanUp() {
        abortController.abort();
      };
    },
    [state.data, manager],
  );

  // --- Error reporting (unchanged) ---
  React.useEffect(
    function reportError() {
      if (error === null) {
        return;
      }
      ErrorMonitoringService.captureError(error);
    },
    [error],
  );

  return (
    <PanelLayout
      isLoading={loadingState === "foreground"}
      state={state}
      layout={layout}
      header={
        <PanelHeader
          backgroundLoading={loadingState === "background"}
          state={state}
        />
      }
    >
      <NoDataMessage
        panelData={state.data}
        onAddDataAction={() => {
          selectPanel(state.id);
          sidebar.openView<RawMessagePanelSettingsSidebarProps>({
            type: SidebarViewType.PanelSettings,
            data: { field: "MessagePaths", spotlightKey: performance.now() },
          });
        }}
      />
      <DataNotFoundMessage panelId={state.id} />
      <NoRecordedDataMessage
        isLoading={loadingState !== false}
        panelData={state.data}
        message={message}
      />
      <FormattedMessage error={error} message={message} />
    </PanelLayout>
  );
}
